import { Alert, Box, CircularProgress, Paper, TextField, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SftpCredentialFields, type SftpCredentials } from "../../components/UIToolkit/molecules/SftpCredentialFields";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { backendService } from "../../services/backend.service";
import PageNotFound from "../pageNotFound/PageNotFound";

export default function SftpSetupPage() {
	const [searchParams] = useSearchParams();
	const linkToken = searchParams.get("link_token") ?? "";
	const [clientId, setClientId] = useState("");
	const [credentials, setCredentials] = useState<SftpCredentials | null>(null);
	const [error, setError] = useState<string | null>(null);

	const { mutate: create, isPending } = useMutation({
		mutationFn: async (id: string) => {
			const response = await backendService.createSftpUserViaToken(id, linkToken);
			return response.data;
		},
		onSuccess: (data) => {
			setCredentials(data);
			setClientId("");
			setError(null);
		},
		onError: (err) => {
			const axiosError = err as AxiosError<{ detail?: string }>;
			setError(axiosError.response?.data?.detail ?? "Something went wrong. Please try again.");
		},
	});

	const handleSubmit = () => {
		const trimmed = clientId.trim();
		if (!trimmed) return;
		setCredentials(null);
		setError(null);
		create(trimmed);
	};

	if (!linkToken) return <PageNotFound />;

	return (
		<Box
			sx={{
				display: "flex",
				justifyContent: "center",
				alignItems: "flex-start",
				minHeight: "100vh",
				pt: 8,
				px: 2,
				bgcolor: "background.default",
			}}
		>
			<Paper sx={{ p: 4, width: "100%", maxWidth: 480 }}>
				<Typography variant="h5" sx={{ mb: 1 }}>
					SFTP User Setup
				</Typography>
				<Typography
					variant="body2"
					sx={{
						color: "text.secondary",
						mb: 3,
					}}
				>
					Enter the client name to provision a new SFTP account.
				</Typography>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<TextField
						label="Client Name"
						value={clientId}
						onChange={(e) => setClientId(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
						fullWidth
						disabled={isPending}
						autoFocus
					/>
					<SystemButton onClick={handleSubmit} disabled={!clientId.trim() || isPending}>
						{isPending ? <CircularProgress size={20} color="inherit" /> : "Create SFTP User"}
					</SystemButton>
				</Box>

				{credentials && (
					<Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
						<Alert severity="success">SFTP user created. Save these credentials — the password will not be shown again.</Alert>
						<SftpCredentialFields credentials={credentials} dataTestId="sftp-setup-credentials" />
					</Box>
				)}
			</Paper>
		</Box>
	);
}
